import { useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, Heading } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  IconButton,
  Input,
  Textarea,
  Badge,
  Skeleton,
  SegmentedControl,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useHeaderRules,
  useSaveHeaderRule,
  useDeleteHeaderRule,
  type HeaderRule,
} from '@/api/manticore';
import { errorMessage } from '@/api/error-message';
import { trackFeatureAction } from '@/lib/analytics-events';
import { ConditionBuilder } from '../_shared/ConditionBuilder';
import { CdnAspectDrift } from '../_shared/VendorDrift';

const PHASES = [
  { value: 'request', label: 'Request' },
  { value: 'response', label: 'Response' },
];
const ACTIONS = [
  { value: 'set', label: 'Set (overwrite)' },
  { value: 'append', label: 'Append' },
  { value: 'remove', label: 'Remove' },
];

type Editing = HeaderRule | 'new' | null;

const blankRule = (): HeaderRule => ({
  id: 0,
  phase: 'response',
  action: 'set',
  header_name: '',
  header_value: '',
  condition: '',
  priority: 100,
});

export function HeaderRulesTab({ domain, domainId }: { domain: string; domainId: string }) {
  const { show } = useToast();
  const { data: rules = [], isLoading } = useHeaderRules(domainId);
  const [editing, setEditing] = useState<Editing>(null);
  const [pendingDelete, setPendingDelete] = useState<HeaderRule | null>(null);

  const save = useSaveHeaderRule(domainId, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Header rule saved' });
      setEditing(null);
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not save header rule', description: errorMessage(e, 'Could not save header rule') }),
  });
  const remove = useDeleteHeaderRule(domainId, {
    onSuccess: () => {
      trackFeatureAction('cdn', 'header_rule', 'delete');
      show({ tone: 'success', title: 'Header rule deleted' });
      setPendingDelete(null);
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not delete header rule', description: errorMessage(e, 'Could not delete header rule') }),
  });

  const columns = useMemo<ColumnDef<HeaderRule, unknown>[]>(
    () => [
      {
        id: 'priority',
        header: 'Priority',
        cell: ({ row }) => (
          <Badge tone="neutral" appearance="subtle" size="sm">
            {row.original.priority}
          </Badge>
        ),
      },
      {
        id: 'phase',
        header: 'Phase',
        cell: ({ row }) => (
          <Badge tone={row.original.phase === 'request' ? 'warning' : 'info'} appearance="subtle" size="sm">
            {row.original.phase}
          </Badge>
        ),
      },
      { id: 'action', header: 'Action', cell: ({ row }) => <span style={{ textTransform: 'capitalize' }}>{row.original.action}</span> },
      {
        id: 'header',
        header: 'Header',
        cell: ({ row }) => (
          <span style={{ fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)', fontSize: 'var(--vn-font-size-sm)' }}>
            {row.original.header_name}
            {row.original.action !== 'remove' && row.original.header_value ? `: ${row.original.header_value}` : ''}
          </span>
        ),
      },
      {
        id: 'condition',
        header: 'Condition',
        cell: ({ row }) => (
          <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>{row.original.condition || 'All requests'}</span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--vn-space-1)' }} onClick={(e) => e.stopPropagation()}>
            <IconButton variant="ghost" size="sm" aria-label="Edit" onClick={() => setEditing(row.original)}>
              <Pencil size={15} />
            </IconButton>
            <IconButton variant="ghost" size="sm" aria-label="Delete" onClick={() => setPendingDelete(row.original)}>
              <Trash2 size={15} />
            </IconButton>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <Section title="Header Rules" description={`Add, modify, or remove request/response headers for ${domain}.`}>
      <CdnAspectDrift domainId={domainId} path="header-rules" />
      <Card>
        <CardHeader
          actions={
            <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={() => setEditing('new')}>
              Add rule
            </Button>
          }
        >
          <CardTitle>Rules</CardTitle>
          <CardDescription>Higher priority runs first.</CardDescription>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <Skeleton style={{ height: 120 }} />
          ) : (
            <DataTable
              columns={columns}
              data={rules}
              getRowId={(r) => String(r.id)}
              onRowClick={(r) => setEditing(r)}
              emptyState={
                <EmptyState
                  size="sm"
                  icon={<Heading size={28} />}
                  title="No header rules"
                  description="Add a rule to rewrite request or response headers."
                  actions={
                    <Button size="sm" startIcon={<Plus size={14} />} onClick={() => setEditing('new')}>
                      Add rule
                    </Button>
                  }
                />
              }
            />
          )}
        </CardBody>
      </Card>

      {editing && (
        <HeaderRuleEditor
          key={editing === 'new' ? 'new' : editing.id}
          initial={editing === 'new' ? blankRule() : editing}
          saving={save.isPending}
          onCancel={() => setEditing(null)}
          onSave={(rule) => save.mutate(rule, { onSuccess: () => trackFeatureAction('cdn', 'header_rule', rule.id ? 'edit' : 'create') })}
        />
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete header rule?"
        description={`Remove the ${pendingDelete?.action} rule for “${pendingDelete?.header_name}”?`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      />
    </Section>
  );
}

function HeaderRuleEditor({
  initial,
  saving,
  onCancel,
  onSave,
}: {
  initial: HeaderRule;
  saving: boolean;
  onCancel: () => void;
  onSave: (rule: HeaderRule) => void;
}) {
  const [form, setForm] = useState<HeaderRule>(initial);
  const [mode, setMode] = useState<'visual' | 'expression'>('visual');
  const set = <K extends keyof HeaderRule>(k: K, v: HeaderRule[K]) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Card style={{ marginTop: 'var(--vn-space-4)' }}>
      <CardHeader>
        <CardTitle>{initial.id ? `Edit rule #${initial.priority}` : 'Create rule'}</CardTitle>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          <Field label="Phase">
            {(p) => (
              <Select value={form.phase} onValueChange={(v) => set('phase', v)}>
                <SelectTrigger {...p}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PHASES.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          <Field label="Action">
            {(p) => (
              <Select value={form.action} onValueChange={(v) => set('action', v)}>
                <SelectTrigger {...p}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          <Field label="Priority">
            {(p) => <Input {...p} type="number" value={String(form.priority)} onChange={(e) => set('priority', Number(e.target.value) || 0)} />}
          </Field>
        </div>
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
          <Field label="Header name" required>
            {(p) => <Input {...p} value={form.header_name} placeholder="X-Custom-Header" onChange={(e) => set('header_name', e.target.value)} />}
          </Field>
          <Field label="Header value">
            {(p) => (
              <Input
                {...p}
                value={form.header_value}
                placeholder="value"
                disabled={form.action === 'remove'}
                onChange={(e) => set('header_value', e.target.value)}
              />
            )}
          </Field>
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--vn-space-2)' }}>
            <span style={{ fontSize: 'var(--vn-font-size-md)', fontWeight: 'var(--vn-font-weight-semibold)' }}>Condition</span>
            <SegmentedControl
              size="sm"
              items={[
                { value: 'visual', label: 'Visual' },
                { value: 'expression', label: 'Expression' },
              ]}
              value={mode}
              onValueChange={(v) => setMode(v as 'visual' | 'expression')}
              aria-label="Condition editor mode"
            />
          </div>
          {mode === 'visual' ? (
            <ConditionBuilder
              value={form.condition}
              onChange={(v) => set('condition', v)}
              onParseFail={() => setMode('expression')}
            />
          ) : (
            <Textarea
              rows={3}
              value={form.condition}
              placeholder='http.uri.path contains "/api"'
              onChange={(e) => set('condition', e.target.value)}
              style={{ fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)' }}
            />
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--vn-space-2)' }}>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button disabled={!form.header_name || saving} onClick={() => onSave(form)}>
            {saving ? 'Saving…' : initial.id ? 'Save rule' : 'Create rule'}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
